import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { RefreshCw, ArrowRight, Calendar, User, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlatformAdminTransitions = () => {
  const { getAuthHeaders } = useAuth();
  const [transitions, setTransitions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/platform-admin/transitions`, { headers: getAuthHeaders() });
        setTransitions(response.data);
      } catch (error) {
        console.error('Failed to fetch transitions:', error);
      }
      setLoading(false);
    };
    fetchData();
  }, [getAuthHeaders]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl uppercase tracking-tight">Coach Transitions</h1>
        <p className="text-muted-foreground">Audit log of all coach switches and transitions</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border/40 p-4 rounded-sm">
          <RefreshCw className="text-primary mb-2" size={20} />
          <p className="text-2xl font-heading">{transitions.length}</p>
          <p className="text-xs text-muted-foreground uppercase">Total Transitions</p>
        </div>
        <div className="bg-card border border-border/40 p-4 rounded-sm">
          <Calendar className="text-blue-400 mb-2" size={20} />
          <p className="text-2xl font-heading">
            {transitions.filter(t => {
              const date = new Date(t.transition_date);
              const now = new Date();
              return (now - date) / (1000 * 60 * 60 * 24) <= 30;
            }).length}
          </p>
          <p className="text-xs text-muted-foreground uppercase">Last 30 Days</p>
        </div>
        <div className="bg-card border border-border/40 p-4 rounded-sm">
          <User className="text-green-400 mb-2" size={20} />
          <p className="text-2xl font-heading">
            {new Set(transitions.map(t => t.athlete_id)).size}
          </p>
          <p className="text-xs text-muted-foreground uppercase">Athletes Affected</p>
        </div>
        <div className="bg-card border border-border/40 p-4 rounded-sm">
          <User className="text-purple-400 mb-2" size={20} />
          <p className="text-2xl font-heading">
            {new Set([...transitions.map(t => t.from_coach_id), ...transitions.map(t => t.to_coach_id)].filter(Boolean)).size}
          </p>
          <p className="text-xs text-muted-foreground uppercase">Coaches Involved</p>
        </div>
      </div>

      {/* Transitions List */}
      <div className="bg-card border border-border/40 rounded-sm">
        <div className="p-4 border-b border-border">
          <h2 className="font-heading text-lg uppercase">Transition History</h2>
        </div>
        
        {transitions.length === 0 ? (
          <div className="p-8 text-center">
            <AlertCircle className="mx-auto text-muted-foreground mb-4" size={32} />
            <p className="text-muted-foreground">No coach transitions recorded yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Transitions occur when athletes switch their primary coach
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border/40">
            {transitions.map((t, idx) => (
              <div key={idx} className="p-4 hover:bg-muted/20 transition-colors">
                <div className="flex items-center gap-4">
                  {/* Athlete */}
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground uppercase">Athlete</p>
                    <p className="font-medium">{t.athlete?.name || 'Unknown'}</p>
                  </div>

                  {/* From Coach */}
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground uppercase">From Coach</p>
                    <p className="font-medium text-red-400">
                      {t.from_coach?.full_name || 'N/A'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t.from_coach?.email || '-'}
                    </p>
                  </div>

                  {/* Arrow */}
                  <div className="px-4">
                    <ArrowRight className="text-primary" size={24} />
                  </div>

                  {/* To Coach */}
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground uppercase">To Coach</p>
                    <p className="font-medium text-green-400">
                      {t.to_coach?.full_name || 'N/A'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t.to_coach?.email || '-'}
                    </p>
                  </div>

                  {/* Date */}
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground uppercase">Date</p>
                    <p className="font-medium">
                      {t.transition_date 
                        ? new Date(t.transition_date).toLocaleDateString()
                        : 'Unknown'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t.transition_date 
                        ? new Date(t.transition_date).toLocaleTimeString()
                        : ''}
                    </p>
                  </div>
                </div>

                {/* Confirmation Info */}
                {t.confirmed_at && (
                  <div className="mt-3 pt-3 border-t border-border/40">
                    <div className="flex items-center gap-2 text-xs text-green-400">
                      <RefreshCw size={12} />
                      <span>Confirmed by athlete at {new Date(t.confirmed_at).toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatformAdminTransitions;
